import { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { FormField, Input, Select } from '../ui/Form';
import { Save, Plus, Trash2 } from 'lucide-react';
import { useAccounts } from '../../hooks/useAccounts';
import { JOURNAL_TYPES } from '../../config/accounting';
import type {
  RecurringEntry,
  CreateRecurringEntryPayload,
  RecurrenceFrequency,
  RecurringEntryTemplateLine,
} from '../../types/recurring-entries';
import { RECURRENCE_FREQUENCY_LABELS } from '../../types/recurring-entries';

interface RecurringEntryFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateRecurringEntryPayload) => Promise<void>;
  entry?: RecurringEntry | null;
}

const emptyLine = (): RecurringEntryTemplateLine => ({
  accountId: '',
  accountCode: '',
  accountName: '',
  debit: 0,
  credit: 0,
  description: '',
  analyticCode: null,
  vatCode: null,
  vatAmount: null,
});

export function RecurringEntryFormModal({ isOpen, onClose, onSubmit, entry }: RecurringEntryFormModalProps) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    frequency: 'monthly' as RecurrenceFrequency,
    nextRunDate: new Date().toISOString().split('T')[0],
    endDate: '',
    journalType: 'general',
    referenceTemplate: '',
  });
  const [templateLines, setTemplateLines] = useState<RecurringEntryTemplateLine[]>([
    emptyLine(),
    emptyLine(),
  ]);

  const { accounts } = useAccounts();

  useEffect(() => {
    if (entry) {
      setFormData({
        name: entry.name,
        description: entry.description || '',
        frequency: entry.frequency,
        nextRunDate: entry.nextRunDate.split('T')[0],
        endDate: entry.endDate ? entry.endDate.split('T')[0] : '',
        journalType: entry.journalType || 'general',
        referenceTemplate: entry.referenceTemplate || '',
      });
      setTemplateLines(
        entry.templateLines.length > 0
          ? entry.templateLines
          : [emptyLine(), emptyLine()]
      );
    } else {
      setFormData({
        name: '',
        description: '',
        frequency: 'monthly',
        nextRunDate: new Date().toISOString().split('T')[0],
        endDate: '',
        journalType: 'general',
        referenceTemplate: '',
      });
      setTemplateLines([emptyLine(), emptyLine()]);
    }
  }, [entry, isOpen]);

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleLineChange = (index: number, field: keyof RecurringEntryTemplateLine, value: string | number | null) => {
    setTemplateLines(prev => {
      const updated = [...prev];
      if (field === 'accountId') {
        const account = accounts.find(a => a.id === value);
        updated[index] = {
          ...updated[index],
          accountId: value as string,
          accountCode: account?.code || '',
          accountName: account?.name || '',
        };
      } else {
        updated[index] = { ...updated[index], [field]: value };
      }
      return updated;
    });
  };

  const addLine = () => {
    setTemplateLines(prev => [...prev, emptyLine()]);
  };

  const removeLine = (index: number) => {
    if (templateLines.length <= 2) return;
    setTemplateLines(prev => prev.filter((_, i) => i !== index));
  };

  const totalDebit = templateLines.reduce((sum, l) => sum + (Number(l.debit) || 0), 0);
  const totalCredit = templateLines.reduce((sum, l) => sum + (Number(l.credit) || 0), 0);
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isBalanced) return;
    setLoading(true);
    try {
      const validLines = templateLines.filter(l => l.accountId && (l.debit > 0 || l.credit > 0));
      await onSubmit({
        name: formData.name,
        description: formData.description || undefined,
        frequency: formData.frequency,
        nextRunDate: formData.nextRunDate,
        endDate: formData.endDate || undefined,
        journalType: formData.journalType || undefined,
        referenceTemplate: formData.referenceTemplate || undefined,
        templateLines: validLines,
      });
      onClose();
    } catch {
      // Error handled by parent
    } finally {
      setLoading(false);
    }
  };

  const journalOptions = Object.entries(JOURNAL_TYPES).map(([key, val]) => ({
    value: key.toLowerCase(),
    label: val.label,
  }));

  const frequencyOptions = Object.entries(RECURRENCE_FREQUENCY_LABELS).map(([value, label]) => ({
    value,
    label,
  }));

  const accountOptions = accounts.map(a => ({
    value: a.id,
    label: `${a.code} — ${a.name}`,
  }));

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={entry ? 'Modifier l\'écriture récurrente' : 'Nouvelle écriture récurrente'}
      size="xl"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Info principale */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="Nom" required htmlFor="rec-name">
            <Input
              id="rec-name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Ex: Loyer mensuel bureau"
              required
            />
          </FormField>

          <FormField label="Fréquence" required htmlFor="rec-frequency">
            <Select
              id="rec-frequency"
              value={formData.frequency}
              onChange={(e) => handleChange('frequency', e.target.value)}
              options={frequencyOptions}
            />
          </FormField>

          <FormField label="Prochaine exécution" required htmlFor="rec-next-date">
            <Input
              id="rec-next-date"
              type="date"
              value={formData.nextRunDate}
              onChange={(e) => handleChange('nextRunDate', e.target.value)}
              required
            />
          </FormField>

          <FormField label="Date de fin" htmlFor="rec-end-date" hint="Laisser vide = indéfini">
            <Input
              id="rec-end-date"
              type="date"
              value={formData.endDate}
              onChange={(e) => handleChange('endDate', e.target.value)}
            />
          </FormField>

          <FormField label="Type de journal" htmlFor="rec-journal">
            <Select
              id="rec-journal"
              value={formData.journalType}
              onChange={(e) => handleChange('journalType', e.target.value)}
              options={journalOptions}
            />
          </FormField>

          <FormField label="Modèle de référence" htmlFor="rec-ref" hint="Ex: LOYER-{YYYY}-{MM}">
            <Input
              id="rec-ref"
              value={formData.referenceTemplate}
              onChange={(e) => handleChange('referenceTemplate', e.target.value)}
              placeholder="LOYER-{YYYY}-{MM}"
            />
          </FormField>
        </div>

        <FormField label="Description" htmlFor="rec-desc">
          <Input
            id="rec-desc"
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Description optionnelle"
          />
        </FormField>

        {/* Lignes modèle */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-foreground">Lignes modèle</h3>
            <Button type="button" variant="outline" size="sm" icon={Plus} onClick={addLine}>
              Ajouter une ligne
            </Button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="pb-2 font-medium text-muted-foreground">Compte</th>
                  <th className="pb-2 font-medium text-muted-foreground text-right w-28">Débit</th>
                  <th className="pb-2 font-medium text-muted-foreground text-right w-28">Crédit</th>
                  <th className="pb-2 font-medium text-muted-foreground">Libellé</th>
                  <th className="pb-2 font-medium text-muted-foreground w-28">Code Analytique</th>
                  <th className="pb-2 font-medium text-muted-foreground w-24">Code TVA</th>
                  <th className="pb-2 font-medium text-muted-foreground text-right w-28">Montant TVA</th>
                  <th className="pb-2 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {templateLines.map((line, idx) => (
                  <tr key={idx}>
                    <td className="py-2 pr-2">
                      <select
                        value={line.accountId}
                        onChange={(e) => handleLineChange(idx, 'accountId', e.target.value)}
                        className="form-select w-full text-xs rounded border-border bg-background text-foreground"
                      >
                        <option value="">Sélectionner un compte</option>
                        {accountOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 px-1">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={line.debit || ''}
                        onChange={(e) => handleLineChange(idx, 'debit', parseFloat(e.target.value) || 0)}
                        className="form-input w-full text-xs text-right rounded border-border bg-background text-foreground"
                        placeholder="0,00"
                      />
                    </td>
                    <td className="py-2 px-1">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={line.credit || ''}
                        onChange={(e) => handleLineChange(idx, 'credit', parseFloat(e.target.value) || 0)}
                        className="form-input w-full text-xs text-right rounded border-border bg-background text-foreground"
                        placeholder="0,00"
                      />
                    </td>
                    <td className="py-2 px-1">
                      <input
                        type="text"
                        value={line.description}
                        onChange={(e) => handleLineChange(idx, 'description', e.target.value)}
                        className="form-input w-full text-xs rounded border-border bg-background text-foreground"
                        placeholder="Libellé"
                      />
                    </td>
                    <td className="py-2 px-1">
                      <input
                        type="text"
                        value={line.analyticCode || ''}
                        onChange={(e) => handleLineChange(idx, 'analyticCode', e.target.value || null)}
                        className="form-input w-full text-xs rounded border-border bg-background text-foreground"
                        placeholder="Code"
                      />
                    </td>
                    <td className="py-2 px-1">
                      <input
                        type="text"
                        value={line.vatCode || ''}
                        onChange={(e) => handleLineChange(idx, 'vatCode', e.target.value || null)}
                        className="form-input w-full text-xs rounded border-border bg-background text-foreground"
                        placeholder="TVA"
                      />
                    </td>
                    <td className="py-2 px-1">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={line.vatAmount || ''}
                        onChange={(e) => handleLineChange(idx, 'vatAmount', parseFloat(e.target.value) || null)}
                        className="form-input w-full text-xs text-right rounded border-border bg-background text-foreground"
                        placeholder="0,00"
                      />
                    </td>
                    <td className="py-2 pl-1">
                      <button
                        type="button"
                        onClick={() => removeLine(idx)}
                        disabled={templateLines.length <= 2}
                        className="p-1 text-muted-foreground hover:text-destructive disabled:opacity-30"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-border font-semibold text-sm">
                  <td className="pt-2 text-right pr-2">Total</td>
                  <td className={`pt-2 px-1 text-right ${!isBalanced ? 'text-destructive' : ''}`}>
                    {totalDebit.toLocaleString('fr-FR', { minimumFractionDigits: 2 })}
                  </td>
                  <td className={`pt-2 px-1 text-right ${!isBalanced ? 'text-destructive' : ''}`}>
                    {totalCredit.toLocaleString('fr-FR', { minimumFractionDigits: 2 })}
                  </td>
                  <td colSpan={4} className="pt-2 px-1">
                    {!isBalanced && (
                      <span className="text-xs text-destructive">
                        Écart : {Math.abs(totalDebit - totalCredit).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}
                      </span>
                    )}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-border">
          <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
            Annuler
          </Button>
          <Button type="submit" variant="primary" icon={Save} isLoading={loading} disabled={!isBalanced}>
            {entry ? 'Mettre à jour' : 'Créer'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
